/**
 * "An enrollment was just created" — raised by the enrollment API calls
 * themselves (api/programEnrollmentAPI.js), so every surface that enrolls an
 * athlete raises it without each one having to remember to.
 *
 * The one listener today is MembershipReactivationPrompt (mounted once in the
 * staff app shell): enrolling an athlete on an INACTIVE membership bills
 * nothing, because monthly dues skip inactive memberships and athletes. On
 * 2026-09-30 a member reported "I haven't been getting monthly membership
 * dues" for exactly that reason, and Dan's rule is that ANY enrollment on an
 * inactive membership must offer to reactivate it.
 *
 * A listener that throws never breaks the enrollment that raised the signal.
 */

export interface EnrollmentCreatedInfo {
  athleteId?: string | null;
  membershipId?: string | null;
}

type Listener = (info: EnrollmentCreatedInfo) => void;

const listeners = new Set<Listener>();

export function notifyEnrollmentCreated(info: EnrollmentCreatedInfo): void {
  if (!info?.athleteId && !info?.membershipId) return;
  listeners.forEach((listener) => {
    try {
      listener(info);
    } catch {
      // never let a prompt break the enrollment itself
    }
  });
}

export function onEnrollmentCreated(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * "A membership (and these athletes) was just reactivated" — raised by
 * MembershipReactivationPrompt after it succeeds.
 *
 * Pages that hold a membership's or athlete's STATUS in a form must reload
 * on it: EditMembershipPage and StaffUpdateAthlete both save the status
 * field they loaded, so a stale "inactive" left on screen would deactivate
 * the membership/athlete again on the next Save — and deactivation cascades
 * into unenrolling every class.
 */
export interface MembershipReactivatedInfo {
  membershipId: string;
  athleteIds: string[];
}

type ReactivatedListener = (info: MembershipReactivatedInfo) => void;

const reactivatedListeners = new Set<ReactivatedListener>();

export function notifyMembershipReactivated(info: MembershipReactivatedInfo): void {
  reactivatedListeners.forEach((listener) => {
    try {
      listener(info);
    } catch {
      // a page failing to refresh must not undo the reactivation toast
    }
  });
}

export function onMembershipReactivated(listener: ReactivatedListener): () => void {
  reactivatedListeners.add(listener);
  return () => {
    reactivatedListeners.delete(listener);
  };
}
